export type SortDirection = "asc" | "desc" | "nosort"
export type SortKey<T> = keyof T // | ((obj: T) => number)

export interface SortRule<T> {
    key: SortKey<T>;
    dir: SortDirection;
    type?: string;
    dataLkpFunc?: (value: unknown) => string | number | undefined;
    comparator?: (a: unknown, b: unknown) => number;
}

export function defaultCompare(a: unknown, b: unknown, type?: SortRule<unknown>["type"]) {
    if (a === b) return 0
    if (a == null) return 1
    if (b == null) return -1

    switch (type) {
        case "string":
            return String(a).localeCompare(String(b))
        case "number":
            return Number(a) - Number(b)
        case "boolean":
            return (a === b) ? 0 : a ? 1 : -1
        case "date":
            return new Date(String(a)).getTime() - new Date(String(b)).getTime()
        case "array":
            return (Array.isArray(a) ? a.length : 0) - (Array.isArray(b) ? b.length : 0)
        default:
            if (a > b) return 1
            if (a < b) return -1
            return 0
    }
}

function sort<T>(a: T, b: T, sortingRules: SortRule<T>[], ruleIndex: number): number {
    const rule = sortingRules[ruleIndex]
    const { key, dir, type, dataLkpFunc, comparator } = rule

    let result = 0
    if (dir !== "nosort") {
        let valA = a[key]
        let valB = b[key]
        if (dataLkpFunc) {
            valA = dataLkpFunc(valA) as T[keyof T]
            valB = dataLkpFunc(valB) as T[keyof T]
        }

        if (comparator) {
            result = comparator(valA, valB)
        } else {
            result = defaultCompare(valA, valB, type)
        }
    }

    if (result !== 0) {
        return dir === "asc" ? result : (-1 * result)
    } else {
        if (ruleIndex === sortingRules.length - 1) {
            return 0
        } else {
            return sort<T>(a, b, sortingRules, ruleIndex + 1)
        }
    }
}

function buildComparator<T>(sortingRules: SortRule<T>[]) {
    return (a: T, b: T) => sort(a, b, sortingRules, 0)
}

export function sortData<T>(data: T[], sortingRules: SortRule<T>[]) {
    if (!data || data.length === 0) return []
    if (sortingRules.length === 0) return data
    const comparator = buildComparator(sortingRules)
    return [...data].sort(comparator)
}